import React, { useState, useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { Link } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';

const ProjectPreview = () => {
  const [hoveredProject, setHoveredProject] = useState(null);
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, threshold: 0.2 });

  const projects = [
    {
      id: 1,
      title: "EcoTrack Analytics",
      category: "Web Application",
      description: "Real-time environmental monitoring dashboard with 3D data visualization and predictive analytics for sustainable business operations.",
      image: "https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=600&h=400&fit=crop",
      tech: ["React", "Three.js", "Node.js", "MongoDB"],
      color: "from-green-500/20 to-emerald-500/20",
      icon: "BarChart3"
    },
    {
      id: 2,
      title: "FinanceFlow Mobile",
      category: "Mobile App",
      description: "AI-powered personal finance management app with interactive 3D spending visualizations and smart budgeting recommendations.",
      image: "https://images.pexels.com/photos/590022/pexels-photo-590022.jpeg?w=600&h=400&fit=crop",
      tech: ["React Native", "TensorFlow", "Firebase", "Stripe"],
      color: "from-blue-500/20 to-cyan-500/20",
      icon: "Smartphone"
    },
    {
      id: 3,
      title: "MedConnect Platform",
      category: "Healthcare System",
      description: "Comprehensive telemedicine platform with 3D anatomy visualization, real-time patient monitoring, and secure communication.",
      image: "https://docpulse.com/wp-content/uploads/2024/02/slider-small-1.jpg",
      tech: ["Vue.js", "WebRTC", "Python", "PostgreSQL"],
      color: "from-purple-500/20 to-pink-500/20",
      icon: "Heart"
    },
    {
      id: 4,
      title: "RetailVision AR",
      category: "AR/VR Solution",
      description: "Augmented reality shopping experience with 3D product visualization, virtual try-on, and immersive store navigation.",
      image: "https://images.unsplash.com/photo-1441986300917-64674bd600d8?w=600&h=400&fit=crop",
      tech: ["Unity", "ARKit", "WebXR", "GraphQL"],
      color: "from-orange-500/20 to-red-500/20",
      icon: "Eye"
    }
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
        delayChildren: 0.3
      }
    }
  };

  const projectVariants = {
    hidden: { 
      opacity: 0, 
      y: 50,
      rotateX: -15
    },
    visible: { 
      opacity: 1, 
      y: 0,
      rotateX: 0,
      transition: {
        duration: 0.8,
        ease: "easeOut"
      }
    }
  };

  return (
    <section ref={ref} className="py-20 bg-gradient-to-b from-background to-muted/20">
      <div className="container mx-auto px-6">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl lg:text-5xl font-bold text-foreground mb-6">
            Featured <span className="text-accent">Projects</span>
          </h2>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
            Explore our portfolio of cutting-edge digital solutions that transform businesses through innovative technology and immersive user experiences.
          </p>
        </motion.div>

        {/* Projects Grid */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate={isInView ? "visible" : "hidden"}
          className="grid grid-cols-1 lg:grid-cols-2 gap-8 max-w-6xl mx-auto"
        >
          {projects?.map((project, index) => (
            <motion.div
              key={project?.id}
              variants={projectVariants}
              className="group relative perspective-1000"
              onMouseEnter={() => setHoveredProject(project?.id)}
              onMouseLeave={() => setHoveredProject(null)}
            >
              <div className={`relative bg-gradient-to-br ${project?.color} backdrop-blur-sm border border-border/50 rounded-2xl overflow-hidden transform-3d transition-all duration-500 ${
                hoveredProject === project?.id ? 'scale-105 shadow-accent' : 'hover:scale-102'
              }`}>
                {/* Project Image */}
                <div className="relative h-64 overflow-hidden">
                  <Image
                    src={project?.image}
                    alt={project?.title}
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-background/80 via-transparent to-transparent"></div>
                  
                  {/* Floating Icon */}
                  <motion.div
                    animate={hoveredProject === project?.id ? {
                      scale: [1, 1.2, 1],
                      rotate: [0, 10, 0]
                    } : {}}
                    transition={{ duration: 0.6 }}
                    className="absolute top-4 right-4 w-12 h-12 bg-accent/90 backdrop-blur-sm rounded-xl flex items-center justify-center"
                  >
                    <Icon name={project?.icon} size={24} color="var(--color-primary)" />
                  </motion.div>
                </div>

                {/* Project Content */}
                <div className="p-6">
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-sm font-medium text-accent bg-accent/10 px-3 py-1 rounded-full">
                      {project?.category}
                    </span>
                    <motion.div
                      animate={hoveredProject === project?.id ? { x: 5 } : { x: 0 }}
                      transition={{ duration: 0.3 }}
                    >
                      {/* <Icon name="ArrowUpRight" size={20} className="text-muted-foreground group-hover:text-accent transition-colors" /> */}
                    </motion.div>
                  </div>

                  <h3 className="text-2xl font-bold text-foreground mb-3 group-hover:text-accent transition-colors">
                    {project?.title}
                  </h3>

                  <p className="text-muted-foreground mb-4 leading-relaxed">
                    {project?.description}
                  </p>

                  {/* Tech Stack */}
                  <div className="flex flex-wrap gap-2">
                    {project?.tech?.map((tech, techIndex) => (
                      <motion.span
                        key={techIndex}
                        initial={{ opacity: 0, scale: 0.8 }}
                        animate={isInView ? { opacity: 1, scale: 1 } : {}}
                        transition={{ delay: 0.1 * techIndex }}
                        className="text-xs font-medium text-muted-foreground bg-muted/50 px-2 py-1 rounded-md"
                      >
                        {tech}
                      </motion.span>
                    ))}
                  </div>
                </div>

                {/* Hover Overlay */}
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: hoveredProject === project?.id ? 1 : 0 }}
                  transition={{ duration: 0.3 }}
                  className="absolute inset-0 bg-gradient-to-br from-accent/5 to-accent/10 backdrop-blur-[1px] pointer-events-none"
                />
              </div>
            </motion.div>
          ))}
        </motion.div>

        {/* View All Projects CTA */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 0.8 }}
          className="text-center mt-16"
        >
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className="inline-flex items-center space-x-2 bg-accent/10 hover:bg-accent/20 text-accent border border-accent/30 hover:border-accent px-8 py-4 rounded-xl font-medium transition-all duration-300"
          >
            <Link to = "/portfolio" className='flex gap-2'>
            <span>View All Projects</span>
            <Icon name="ArrowRight" size={20} />
            </Link>
          </motion.button>
        </motion.div>
      </div>
    </section>
  );
};

export default ProjectPreview;